<template>
  <ColumnFilterCell
    v-model:open="open"
    :summary="summary"
    :active="active"
    :hovered="hovered"
    test-id-prefix="column-filter"
    @positioned="builder?.focus()"
    @closed="filter.flush()"
    @clear="filter.clear"
  >
    <div :class="[$style.popup, config.kind === 'date' && $style.popupDate]" test-id="column-filter-popup">
      <SCAConditionBuilder
        ref="builder"
        :kind="config.kind"
        :operator-options="operatorOptions"
        :default-operator="filter.defaultOperator.value"
        :date-format="config.kind === 'date' ? config : undefined"
        :rows="filter.rows.value"
        @change="filter.change"
        @submit="open = false"
      />
    </div>
  </ColumnFilterCell>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import ColumnFilterCell from "../ColumnFilterCell.vue";
import type { ColFilterGroup } from "../filterModel";
import type { ConditionColumnFilterConfig } from "../types";
import { summarizeConditions } from "./conditionSummary";
import { operatorOptionsFor } from "./operators";
import SCAConditionBuilder from "./SCAConditionBuilder.vue";
import { useConditionFilter } from "./useConditionFilter";

const props = defineProps<{
  colKey: string;
  config: ConditionColumnFilterConfig;
  model: ColFilterGroup[] | null;
  /** Pointer is over the filter cell — the icons only show up then (or while in use). */
  hovered: boolean;
}>();

const emit = defineEmits<{
  "model-change": [model: ColFilterGroup[] | null];
}>();

const open = ref(false);
const builder = ref<InstanceType<typeof SCAConditionBuilder> | null>(null);

const filter = useConditionFilter({
  colKey: () => props.colKey,
  config: () => props.config,
  model: () => props.model,
  onModelChange: (model) => emit("model-change", model),
});

const operatorOptions = computed(() =>
  props.config.kind === "date"
    ? operatorOptionsFor("date")
    : operatorOptionsFor(props.config.kind, props.config.excludedOperators, props.config.disabledOperators),
);

const summary = computed(() => summarizeConditions(filter.rows.value, { formatTerm: filter.formatTerm.value }));

const active = computed(() => summary.value !== "");
</script>

<style module lang="scss">
.popup {
  width: 200px;
}

.popupDate {
  width: fit-content;
}

.popupDate > * {
  min-width: 200px;
}
</style>
